import { addFormatNRC, addFormatToNIT } from "@/helpers/misc";
import { formatNumber } from "@/helpers/money";

export const DTE_TYPE_LABELS = {
  FCF: "Factura Consumidor Final",
  CCF: "Comprobante de Credito Fiscal",
  FEX: "Factura de Exportacion",
  FNS: "Factura Sujeto Excluido",
  DNC: "Nota de Credito",
  DND: "Nota de Debito",
  CDD: "Comprobante de Donacion",
  DNR: "Nota de Remision",
};

export const MODEL_LABELS = {
  1: "Previo",
  2: "Diferido",
};

export const TRANSMISSION_LABELS = {
  1: "Normal",
  2: "Contingencia",
};

export const DOCUMENT_TYPE_LABELS = {
  1: "DUI",
  2: "NIT",
  3: "Pasaporte",
  4: "Carnet de Residente",
  5: "Otro",
};

export function asNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function cleanText(value, fallback = "---") {
  if (value === null || value === undefined) {
    return fallback;
  }

  const stringValue = String(value).trim();
  if (!stringValue || ["null", "undefined", "NaN"].includes(stringValue)) {
    return fallback;
  }

  return stringValue;
}

export function cleanAssetUrl(value) {
  return cleanText(value, "");
}

export function htmlToText(value) {
  if (!value) {
    return "";
  }

  return String(value)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function formatMoney(value, currency = "$", decimals = 2) {
  return `${currency} ${formatNumber(asNumber(value), decimals)}`;
}

export function formatPlainMoney(value, decimals = 2) {
  return formatNumber(asNumber(value), decimals);
}

export function formatDocumentTypeLabel(id) {
  return DOCUMENT_TYPE_LABELS[id] || "Documento";
}

export function formatNit(value) {
  return addFormatToNIT(cleanText(value, ""));
}

export function formatNrc(value) {
  return addFormatNRC(cleanText(value, ""));
}

export function formatInvoiceTypeLabel(code) {
  return DTE_TYPE_LABELS[code] || cleanText(code, "Documento Tributario Electronico");
}

export function formatModelLabel(code) {
  return MODEL_LABELS[code] || cleanText(code, "---");
}

export function formatTransmissionLabel(code) {
  return TRANSMISSION_LABELS[code] || cleanText(code, "---");
}

export function sanitizeFilenameSegment(value) {
  return cleanText(value, "documento").replace(/[\\/:*?"<>|]+/g, "").replace(/\s+/g, " ").trim();
}

export function buildFileBaseName(header = {}) {
  const id = cleanText(header.ID_Invoice, "DTE");
  const date = cleanText(header.InvoiceDate, "").replace(/\//g, "-");
  const name = sanitizeFilenameSegment(header.InvoiceName);
  return [id, date, name].filter(Boolean).join("_");
}

export function extractDateParts(dateString) {
  const raw = cleanText(dateString, "");
  const parts = raw.split("/");

  if (parts.length !== 3) {
    return null;
  }

  const day = Number(parts[0]);
  const month = Number(parts[1]) - 1;
  const year = Number(parts[2]);

  if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) {
    return null;
  }

  return { day, month, year };
}

export function calculateDueDate(dateString, creditDayText) {
  const dateParts = extractDateParts(dateString);
  if (!dateParts) {
    return "";
  }

  const creditDays = Number(String(creditDayText || "").match(/\d+/)?.[0] || 0);
  const date = new Date(dateParts.year, dateParts.month, dateParts.day);
  date.setDate(date.getDate() + creditDays);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

export function buildMhQueryDate(dateString) {
  const dateParts = extractDateParts(dateString);
  if (!dateParts) {
    return "";
  }

  const day = String(dateParts.day).padStart(2, "0");
  const month = String(dateParts.month + 1).padStart(2, "0");
  return `${dateParts.year}-${month}-${day}`;
}

export function buildMhQrUrl(rawData = {}) {
  const header = rawData.header || {};
  const branch = rawData.branch || {};
  const issueDate = buildMhQueryDate(header.InvoiceDate);

  if (!header.CodigoGeneracion || !issueDate) {
    return "";
  }

  const ambiente = Number(branch.AuthFeMode) === 1 ? "01" : "00";
  return `https://admin.factura.gob.sv/consultaPublica?ambiente=${ambiente}&codGen=${header.CodigoGeneracion}&fechaEmi=${issueDate}`;
}

export function getStoredUserTheme() {
  try {
    const user = JSON.parse(localStorage.getItem("user")) || {};
    return {
      brand: user.specialinvoicescolor || "#0f4c81",
      brandContrast: user.specialInvoicesTitleColor || "#ffffff",
      accent: "#d9a441",
      surface: "#f5f8fc",
      border: "#c7d5e5",
      text: "#1b2a3a",
      muted: "#6c7b8a",
    };
  } catch (error) {
    return {
      brand: "#0f4c81",
      brandContrast: "#ffffff",
      accent: "#d9a441",
      surface: "#f5f8fc",
      border: "#c7d5e5",
      text: "#1b2a3a",
      muted: "#6c7b8a",
    };
  }
}
